import React, { useState } from 'react';
import styled from 'styled-components';
import { useTranslation } from 'react-i18next';
import { AxiosError } from 'axios';
import FileUploadButton from '../../../components/buttons/FileUploadButton';
import DropdownWithLabel from '../../../components/inputs/DropdownWithLabel';
import RectangularButton from '../../../components/buttons/RectangularButton';
import { PopupContainer, PopupHeader } from '../../../components/dialogs/PopupCommons';
import { SectionHeader } from '../../../components/typography/Headers';
import { usePopup } from '../../../providers/PopupProvider';
import { useError } from '../../../providers/useError';
import LoadingOverlay from '../../../components/loading/LoadingOverlay';
import { paymentService } from '../../../services/Payment/paymentService';

const Container = styled.div`
  display: flex;
  flex-direction: column;
  gap: 15px;
  min-width: 400px;
  padding: 20px;
`;

type UploadMethod = 'add' | 'refresh' | 'delete';

interface Props {
  onUploadSuccess?: () => void;
}

const OrganizationUploadPopup = ({ onUploadSuccess }: Props) => {
  const { t } = useTranslation('manageContent');
  const { hidePopover, showSuccessPopover } = usePopup();
  const { handleError } = useError();
  const [file, setFile] = useState<File | undefined>();
  const [method, setMethod] = useState<UploadMethod | undefined>();
  const [gzip, setGzip] = useState(false);
  const [isLoading, setIsLoading] = useState(false);

  const handleFileSelect = (selectedFile: File | undefined) => {
    setFile(selectedFile);
    if (selectedFile) {
      const fileName = selectedFile.name.toLowerCase();
      const isGzipFile = fileName.endsWith('.gz') || fileName.endsWith('.gzip');
      setGzip(isGzipFile);
    } else {
      setGzip(false);
    }
  };

  const methodsMap = new Map<UploadMethod, string>([
    ['add', t('organizations.upload.methods.add')],
    ['refresh', t('organizations.upload.methods.refresh')],
    ['delete', t('organizations.upload.methods.delete')],
  ]);

  const handleUpload = async () => {
    if (!file || !method) {
      return;
    }

    setIsLoading(true);
    try {
      switch (method) {
        case 'add':
          await paymentService.add(file, gzip);
          break;
        case 'refresh':
          await paymentService.refresh(file, gzip);
          break;
        case 'delete':
          await paymentService.deleteBulk(file, gzip);
          break;
      }

      hidePopover();
      showSuccessPopover(t('organizations.upload.success'));
      if (onUploadSuccess) {
        onUploadSuccess();
      }
    } catch (error) {
      handleError(error as AxiosError, t('organizations.upload.error'));
    } finally {
      setIsLoading(false);
    }
  };

  const isUploadDisabled = !file || !method || isLoading;

  return (
    <PopupContainer>
      <PopupHeader>
        <SectionHeader>{t('organizations.upload.header')}</SectionHeader>
      </PopupHeader>
      <Container>
        <FileUploadButton
          label={t('organizations.upload.choose-file')}
          onFileSelect={handleFileSelect}
          accept='.csv,.gz,text/csv,application/gzip'
        />
        <DropdownWithLabel
          label={t('organizations.upload.method')}
          optionsMap={methodsMap}
          value={method}
          onChange={value => setMethod(value as UploadMethod)}
          placeholder={t('organizations.upload.method')}
        />
        <RectangularButton
          label={t('organizations.upload.upload-btn')}
          onClick={handleUpload}
          disabled={isUploadDisabled}
        />
      </Container>
      {isLoading && <LoadingOverlay />}
    </PopupContainer>
  );
};

export default OrganizationUploadPopup;
